import { useCallback, useState } from 'react'

// Estado dos "spinners" das telas: quais chaves de ação estão em voo (chaves de
// `lib/actionKeys`) e quais jornadas estão em confirmação.
//
// Os leitores (`isActionPending` / `isConfirmingJourney`) trocam de referência quando o
// conjunto correspondente muda. Isso importa: o App monta o objeto `actions` num useMemo
// consumido por 6+ telas — leitor de referência fixa obrigaria o memo a depender do
// estado bruto, e um dia alguém esquece uma dep e o botão fica preso no estado antigo.
export interface PendingActionsApi {
  pendingActions: Set<string>
  isActionPending: (key: string) => boolean
  setActionPending: (key: string, value: boolean) => void
  confirmingJourneyIds: Set<string>
  isConfirmingJourney: (aggregatorId: string) => boolean
  setJourneyConfirming: (aggregatorId: string, value: boolean) => void
}

// Devolve o mesmo Set quando nada muda: setState com o mesmo objeto não re-renderiza.
function toggle(current: Set<string>, key: string, present: boolean): Set<string> {
  if (current.has(key) === present) { return current }
  const next = new Set(current)
  if (present) { next.add(key) } else { next.delete(key) }
  return next
}

export function usePendingActions(): PendingActionsApi {
  const [pendingActions, setPendingActions] = useState<Set<string>>(() => new Set())
  const [confirmingJourneyIds, setConfirmingJourneyIds] = useState<Set<string>>(() => new Set())

  const setActionPending = useCallback((key: string, value: boolean) => {
    setPendingActions((current) => toggle(current, key, value))
  }, [])

  const isActionPending = useCallback((key: string) => pendingActions.has(key), [pendingActions])

  const setJourneyConfirming = useCallback((aggregatorId: string, value: boolean) => {
    setConfirmingJourneyIds((current) => toggle(current, aggregatorId, value))
  }, [])

  const isConfirmingJourney = useCallback(
    (aggregatorId: string) => confirmingJourneyIds.has(aggregatorId),
    [confirmingJourneyIds],
  )

  return {
    pendingActions,
    isActionPending,
    setActionPending,
    confirmingJourneyIds,
    isConfirmingJourney,
    setJourneyConfirming,
  }
}
